import { Loader } from '@10xLabs/react-web/components/Loader'
import { useMutation } from '@apollo/client'
import {
  DndContext,
  useSensor,
  TouchSensor,
  useSensors,
  MouseSensor,
  DragEndEvent,
  closestCenter,
  DragOverlay,
  DragStartEvent,
  DragOverEvent
} from '@dnd-kit/core'
import { ReactElement, useState } from 'react'
import { createPortal } from 'react-dom'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { RoutingProjectPlanStatus } from 'gql/generated/graphql'
import { DatasetLocationsList } from 'features/RoutingProjects/components/RoutingProjectSidebar/DatasetLocationsList'
import { RoutingProjectSidebarActions } from 'features/RoutingProjects/components/RoutingProjectSidebar/Actions'
import { SelectRoutingProjecPlan } from 'features/RoutingProjects/components/SelectRoutingProjectPlan'
import {
  IDraggedLocation,
  ILocationRemove,
  IRoutingProjectDataset,
  IRoutingProjectDatasetLocation,
  IRoutingProjectModel,
  IRoutingProjectPlan,
  IRoutingProjectPlanModel
} from 'features/RoutingProjects/types'
import EmptyState from 'components/v2/EmptyState'
import { Button } from 'components/v2/Button'
import { isEmpty, isNull, uniq } from 'lodash'
import { OptimizedRoutesList } from 'features/RoutingProjects/components/RoutingProjectSidebar/OptimizedRoutesList'
import { OrphansLocationsCard } from 'features/RoutingProjects/components/RoutingProjectSidebar/OrphansLocationsCard'
import { OrphansLocationsList } from 'features/RoutingProjects/components/RoutingProjectSidebar/OrphansLocationsList'
import { LocationItem } from 'features/RoutingProjects/components/LocationItem'
import {
  moveLocationInsidePlanOptimizationBetweenContainers,
  moveLocationInsidePlanOptimizationInsideSameContainer,
  removeLocationFromPlan,
  updateRoutesPolyline
} from 'features/RoutingProjects/models'

import { REMOVE_ROUTING_PROJECT_PLAN_DATASET_LOCATIONS } from 'features/RoutingProjects/mutations'
import { useMap } from '@vis.gl/react-google-maps'
import { adjustMapBoundsByRoutes } from 'features/RoutingProjects/utils'
import { usePlanContext } from 'features/RoutingProjects/containers/PlanContainer'

const SidebarWrapper = styled.div`
  position: relative;
`

const SidebarContainer = styled.div<{ isExpanded: boolean }>`
  padding: 16px;
  height: ${({ isExpanded }): string => (isExpanded ? 'calc(100% - 32px);' : 'min-content')};
  display: grid;
  grid-template-rows: min-content ${({ isExpanded }): string => (isExpanded ? '1fr' : '')};
  gap: 20px;
  position: ${({ isExpanded }): string => (isExpanded ? 'relative' : 'absolute')};
  z-index: ${({ isExpanded }): string => (isExpanded ? '0' : '1')};
  background-color: #fff;
  top: ${({ isExpanded }): string => (isExpanded ? '0' : '24px')};
  left: ${({ isExpanded }): string => (isExpanded ? '0' : '13px')};
  border-radius: 16px;
  min-width: 360px;
`

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const SidebarContent = styled.div`
  height: 0;
  min-height: 100%;
  overflow: auto;
`

const OrphansLocationsListWrapper = styled.div`
  z-index: 1;
  position: absolute;
  top: 166px;
  right: 0;
  transform: translateX(100%);
  max-height: calc(100% - 166px);
  overflow: auto;
`

const OrphansLocationsListContainer = styled.div`
  border-radius: 0px 16px 16px 0px;
  border: 1px solid #e3e3e3;
  background: #fff;
  padding: 24px;
  width: 360px;
`

interface IRoutingProjectSidebarProps {
  routingProjectModel: IRoutingProjectModel
  planModel: IRoutingProjectPlanModel
  planLoading: boolean
  refetchPlan: () => void
  isExpanded: boolean
  setIsExpanded: (isExpanded: boolean) => void
  selectedPlan: IRoutingProjectPlan | null
  setSelectedPlan: (plan: IRoutingProjectPlan | null) => void
  setPlanModel: (planModel: IRoutingProjectPlanModel) => void
  visibleRoutesIds: string[]
  setVisibleRoutesIds: (visibleRoutesIds: string[]) => void
}

export const RoutingProjectSidebar = ({
  routingProjectModel,
  planModel,
  planLoading,
  refetchPlan,
  isExpanded,
  setIsExpanded,
  selectedPlan,
  setSelectedPlan,
  setPlanModel,
  visibleRoutesIds,
  setVisibleRoutesIds
}: IRoutingProjectSidebarProps): ReactElement => {
  const map = useMap()
  const { saveUpdatedRoutes } = usePlanContext()
  const sensors = useSensors(useSensor(TouchSensor), useSensor(MouseSensor))
  const [planStatus, setPlanStatus] = useState<RoutingProjectPlanStatus>(RoutingProjectPlanStatus.Simulation)
  const [isShowingOrphansLocationsList, setIsShowingOrphansLocationsList] = useState(false)
  const [draggedLocation, setDraggedLocation] = useState<IDraggedLocation | null>(null)

  const [removeRoutingProjectDatasetLocations] = useMutation(REMOVE_ROUTING_PROJECT_PLAN_DATASET_LOCATIONS, {
    onError() {
      // Do nothing
    }
  })
  const datasetIsUploading = !planLoading && selectedPlan?.id && isEmpty(planModel.dataset?.locations)
  const datasetReady = !planLoading && selectedPlan?.id && !isEmpty(planModel.dataset?.locations)
  const canSelectOrphans =
    !isEmpty(planModel.optimization?.routes) && !isEmpty(planModel.optimization?.skippedLocations) && !planLoading

  const handleDragStart = (event: DragStartEvent): void => {
    const { active } = event
    if (isEmpty(draggedLocation)) {
      setDraggedLocation({
        sortableItemId: active.id.toString(),
        location: active.data.current?.location as IRoutingProjectDatasetLocation,
        locationIndex: active.data.current?.locationIndex as number,
        routeIndex: active.data.current?.routeIndex as number | null
      })
    }
  }

  const handleDragOver = (event: DragOverEvent): void => {
    const { active, over } = event

    const draggedLocationId = active.data.current?.locationId
    const overLocationId = over?.data.current?.locationId
    const draggedLocationRouteIndex = active.data.current?.routeIndex
    const overRouteIndex = over?.data.current?.routeIndex

    if (!over || draggedLocationRouteIndex === overRouteIndex) {
      return
    }

    const newPlan = moveLocationInsidePlanOptimizationBetweenContainers({
      plan: planModel,
      locationId: draggedLocationId,
      previousRouteIndex: draggedLocationRouteIndex,
      droppedLocationId: overLocationId,
      droppedRouteIndex: overRouteIndex
    })
    setPlanModel({
      ...planModel,
      ...newPlan
    })
  }

  const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
    let newPlanModel = planModel
    setDraggedLocation(null)
    const { active, over } = event
    const draggedLocationRouteIndex = active.data.current?.routeIndex
    const overLocationRouteIndex = over?.data.current?.routeIndex
    if (active.id !== over?.id && overLocationRouteIndex === draggedLocationRouteIndex) {
      const newPlan = moveLocationInsidePlanOptimizationInsideSameContainer(
        planModel,
        active.data.current?.locationId,
        over?.data.current?.locationId,
        draggedLocationRouteIndex
      )
      newPlanModel = {
        ...planModel,
        ...newPlan
      }
      setPlanModel({
        ...planModel,
        ...newPlan
      })
    }
    const updatedRoutesIndex = uniq(
      [draggedLocation?.routeIndex, overLocationRouteIndex].filter((index) => !isNull(index))
    )

    const updatedPlanModel = await updateRoutesPolyline(newPlanModel, updatedRoutesIndex)
    setPlanModel(updatedPlanModel)
    if (map && !isEmpty(updatedRoutesIndex)) {
      setTimeout(() => {
        adjustMapBoundsByRoutes({
          map,
          routes: updatedPlanModel.optimization.routes.filter((_, index) => updatedRoutesIndex.includes(index))
        })
      }, 1)
    }
    saveUpdatedRoutes(updatedPlanModel, updatedRoutesIndex)
  }

  const handleLocationRemove = async ({ locationId }: ILocationRemove): Promise<void> => {
    const routesAffectedIndex = planModel.optimization?.routes.reduce<number[]>((acc, route, index) => {
      const hasLocation = route.stops.some((stop) => stop.location.id === locationId)
      if (hasLocation) {
        acc.push(index)
      }
      return acc
    }, [])
    const newPlan = removeLocationFromPlan({
      plan: planModel,
      locationId
    })
    const newPlanModel = {
      ...planModel,
      ...newPlan
    }
    setPlanModel(newPlanModel)

    const updatedPlanModel = await updateRoutesPolyline(newPlanModel, routesAffectedIndex)
    setPlanModel(updatedPlanModel)
    if (map) {
      setTimeout(() => {
        adjustMapBoundsByRoutes({
          map,
          routes: updatedPlanModel.optimization.routes.filter((_, index) => routesAffectedIndex.includes(index))
        })
      }, 1)
    }

    await saveUpdatedRoutes(updatedPlanModel, routesAffectedIndex)
    removeRoutingProjectDatasetLocations({
      variables: {
        input: {
          id: routingProjectModel.id,
          plan: {
            id: planModel.id,
            dataset: {
              locationIDs: [locationId]
            }
          }
        }
      }
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <SidebarWrapper>
        <SidebarContainer isExpanded={isExpanded}>
          <SidebarHeader>
            <Heading.H3>
              <FormattedMessage id="routingProject.plans.title" defaultMessage="Plans" />
            </Heading.H3>
            <RoutingProjectSidebarActions
              isSidebarExpanded={isExpanded}
              setIsSidebarExpanded={setIsExpanded}
              routingProjectModel={routingProjectModel}
              setSelectedPlan={setSelectedPlan}
              setPlanStatus={setPlanStatus}
            />
          </SidebarHeader>
          {isExpanded && (
            <SidebarContent>
              <Row>
                <Column>
                  <SelectRoutingProjecPlan
                    routingProjectModel={routingProjectModel}
                    selectedPlan={selectedPlan}
                    setSelectedPlan={setSelectedPlan}
                    planStatus={planStatus}
                    setPlanStatus={setPlanStatus}
                  />
                </Column>
              </Row>
              {planLoading && (
                <Row>
                  <Column>
                    <EmptyState.Container>
                      <Loader />
                    </EmptyState.Container>
                  </Column>
                </Row>
              )}
              {datasetIsUploading && (
                <Row>
                  <Column>
                    <EmptyState.Container>
                      <EmptyState.Description>
                        <FormattedMessage
                          id="routingProject.plan.dataset.empty.description"
                          defaultMessage="The locations are not uploaded yet."
                        />
                      </EmptyState.Description>
                      <EmptyState.Action>
                        <Button
                          type="button"
                          kind="primary"
                          small
                          onClick={(): void => {
                            refetchPlan()
                          }}
                        >
                          <FormattedMessage id="routingProject.plan.dataset.empty.action" defaultMessage="Retry" />
                        </Button>
                      </EmptyState.Action>
                    </EmptyState.Container>
                  </Column>
                </Row>
              )}
              {datasetReady && (
                <>
                  {canSelectOrphans && (
                    <Row>
                      <Column>
                        <OrphansLocationsCard
                          skippedLocationsCount={planModel.optimization?.skippedLocations.length}
                          setIsShowingOrphansLocationsList={setIsShowingOrphansLocationsList}
                        />
                      </Column>
                    </Row>
                  )}
                  <Row>
                    <Column>
                      {!isEmpty(planModel.optimization?.routes) ? (
                        <OptimizedRoutesList
                          routingProjectId={routingProjectModel.id}
                          routes={planModel.optimization?.routes}
                          draggedLocation={draggedLocation}
                          onLocationRemove={handleLocationRemove}
                          planModel={planModel}
                          setPlanModel={setPlanModel}
                          visibleRoutesIds={visibleRoutesIds}
                          setVisibleRoutesIds={setVisibleRoutesIds}
                        />
                      ) : (
                        <DatasetLocationsList dataset={planModel.dataset as IRoutingProjectDataset} />
                      )}
                    </Column>
                  </Row>
                </>
              )}
            </SidebarContent>
          )}
        </SidebarContainer>
        {isShowingOrphansLocationsList && canSelectOrphans && (
          <OrphansLocationsListWrapper>
            <OrphansLocationsListContainer>
              <OrphansLocationsList
                skippedLocations={planModel.optimization?.skippedLocations}
                draggedLocation={draggedLocation}
              />
            </OrphansLocationsListContainer>
          </OrphansLocationsListWrapper>
        )}
      </SidebarWrapper>
      {createPortal(
        <DragOverlay>
          {!isEmpty(draggedLocation) ? (
            <LocationItem
              location={draggedLocation.location}
              locationIndex={draggedLocation.locationIndex}
              routeIndex={draggedLocation.routeIndex}
              draggable
            />
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  )
}
